/**
 * ============================================================================
 * Transaction Formatters - Business logic utilities
 * ============================================================================
 *
 * Extracted from JSX for testability and reusability
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from './currency';
import type { Transaction, TransactionType } from '../types/transactions';

/**
 * Format currency amount
 */
export function formatCurrency(amount: number | string | undefined | null): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
  if (isNaN(num)) return `${CURRENCY_SYMBOL}0`;
  return `${CURRENCY_SYMBOL}${num.toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

/**
 * Format date for display (with time)
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get transaction type display info
 */
export function getTransactionTypeInfo(type: TransactionType): {
  label: string;
  colorClass: string;
} {
  const typeMap = {
    booking: { label: 'One-time', colorClass: 'bg-gray-100 text-gray-700' },
    recurring: { label: 'Recurring', colorClass: 'bg-indigo-100 text-indigo-700' },
    membership: { label: 'Membership', colorClass: 'bg-amber-100 text-amber-700' },
    refund: { label: 'Refund', colorClass: 'bg-purple-100 text-purple-700' },
    payout: { label: 'Payout', colorClass: 'bg-orange-100 text-orange-700' },
    debt: { label: 'Debt', colorClass: 'bg-red-100 text-red-700' },
  };

  return typeMap[type] || typeMap.booking;
}

/**
 * Get status display info
 */
export function getStatusInfo(status: string | null | undefined): {
  label: string;
  colorClass: string;
} {
  const statusMap: Record<string, { label: string; colorClass: string }> = {
    completed: { label: 'Completed', colorClass: 'bg-green-100 text-green-700' },
    active: { label: 'Active', colorClass: 'bg-blue-100 text-blue-700' },
    processed: { label: 'Processed', colorClass: 'bg-green-100 text-green-700' },
    pending: { label: 'Pending', colorClass: 'bg-orange-100 text-orange-700' },
    cancelled: { label: 'Cancelled', colorClass: 'bg-red-100 text-red-700' },
    refunded: { label: 'Refunded', colorClass: 'bg-purple-100 text-purple-700' },
    // Recurring instance statuses
    scheduled: { label: 'Scheduled', colorClass: 'bg-blue-100 text-blue-700' },
    rescheduled: { label: 'Rescheduled', colorClass: 'bg-amber-100 text-amber-700' },
    // Backend payout statuses
    held: { label: 'Held', colorClass: 'bg-yellow-100 text-yellow-700' },
    eligible: { label: 'Eligible', colorClass: 'bg-green-100 text-green-700' },
    paid: { label: 'Paid', colorClass: 'bg-green-100 text-green-700' },
    processing: { label: 'Processing', colorClass: 'bg-blue-100 text-blue-700' },
    // Other backend statuses
    expired: { label: 'Expired', colorClass: 'bg-gray-100 text-gray-700' },
    cleared: { label: 'Cleared', colorClass: 'bg-green-100 text-green-700' },
  };

  // Handle null/undefined status
  if (!status) {
    return { label: 'Unknown', colorClass: 'bg-gray-100 text-gray-700' };
  }

  return statusMap[status.toLowerCase()] || statusMap.completed;
}

/**
 * Export transactions to CSV
 */
export function exportTransactionsToCSV(transactions: Transaction[]): void {
  if (transactions.length === 0) {
    throw new Error('No transactions to export');
  }

  const headers = [
    'Date',
    'Type',
    'Reference',
    'Customer',
    'Sport',
    'Description',
    'Credit (+)',
    'Debit (-)',
    'Balance',
    'Status',
  ];

  const rows = transactions.map((t) => [
    formatDate(t.date),
    getTransactionTypeInfo(t.type).label,
    t.booking_reference || t.transfer_reference || 'N/A',
    t.customer || 'N/A',
    t.sport || 'N/A',
    t.description,
    t.is_credit ? (t.amount ?? 0).toFixed(2) : '0.00',
    !t.is_credit ? (t.amount ?? 0).toFixed(2) : '0.00',
    (t.balance ?? 0).toFixed(2),
    getStatusInfo(t.status).label,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  // Download CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Filter transactions by search query
 */
export function filterTransactionsBySearch(
  transactions: Transaction[],
  searchQuery: string
): Transaction[] {
  if (!searchQuery.trim()) return transactions;

  const query = searchQuery.toLowerCase();

  return transactions.filter((t) =>
    t.booking_reference?.toLowerCase().includes(query) ||
    t.transfer_reference?.toLowerCase().includes(query) ||
    t.description.toLowerCase().includes(query) ||
    t.customer?.toLowerCase().includes(query) ||
    t.sport?.toLowerCase().includes(query) ||
    t.venue?.toLowerCase().includes(query)
  );
}
